import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Loader2, Volume2, RotateCcw } from 'lucide-react';

/**
 * ReflectionAudioPlayer — streams a narrated version of the current
 * reflection from GET /api/tts/reflection/:slug (ElevenLabs, cached
 * server-side by content hash).
 *
 * The audio element is created lazily so we never bill for a request
 * unless the visitor actually clicks Listen.
 */
const fmt = (s) => {
  if (!s || Number.isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${ss}`;
};

const ReflectionAudioPlayer = ({ slug }) => {
  const [state, setState] = useState('idle'); // idle | loading | ready | playing | paused | error
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState('');
  const audioRef = useRef(null);
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/tts/reflection/${slug}`;

  const attachEvents = (el) => {
    el.addEventListener('loadedmetadata', () => setDuration(el.duration || 0));
    el.addEventListener('timeupdate', () => setCurrent(el.currentTime || 0));
    el.addEventListener('ended', () => setState('paused'));
    el.addEventListener('play', () => setState('playing'));
    el.addEventListener('pause', () => {
      if (!el.ended) setState('paused');
    });
    el.addEventListener('error', () => {
      setState('error');
      setError('Audio failed to load.');
    });
  };

  const toggle = async () => {
    if (state === 'idle' || state === 'error') {
      setState('loading');
      setError('');
      try {
        const el = new Audio(url);
        el.preload = 'auto';
        audioRef.current = el;
        attachEvents(el);
        el.addEventListener('canplay', () => {
          if (audioRef.current === el) el.play().catch(() => setState('error'));
        }, { once: true });
      } catch {
        setState('error');
        setError('Could not start playback.');
      }
      return;
    }
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      try { await el.play(); } catch { /* ignore */ }
    } else {
      el.pause();
    }
  };

  const restart = () => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = 0;
    el.play().catch(() => {});
  };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    el.currentTime = Math.max(0, Math.min(duration, pct * duration));
  };

  useEffect(() => {
    return () => {
      const el = audioRef.current;
      if (el) {
        el.pause();
        el.src = '';
        audioRef.current = null;
      }
    };
  }, []);

  const isPlaying = state === 'playing';
  const isLoading = state === 'loading';
  const canScrub = state === 'playing' || state === 'paused';

  return (
    <div
      className="mb-10 rounded-2xl glass-strong border border-[#EF5B5B]/25 p-4 sm:p-5 flex items-center gap-4"
      data-testid="reflection-audio-player"
      style={{ boxShadow: '0 20px 60px -30px rgba(239,91,91,0.4)' }}
    >
      <motion.button
        type="button"
        onClick={toggle}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label={isPlaying ? 'Pause narration' : 'Play narration'}
        data-testid="reflection-audio-toggle"
        className="w-12 h-12 shrink-0 rounded-full btn-red inline-flex items-center justify-center"
      >
        {isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : isPlaying ? (
          <Pause className="w-5 h-5" fill="currentColor" />
        ) : (
          <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
        )}
      </motion.button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <Volume2 className="w-3.5 h-3.5 text-[#EF5B5B]" />
          <span className="text-[10px] sm:text-[11px] font-black tracking-[0.3em] uppercase text-[#EF5B5B]">
            Listen to this reflection
          </span>
        </div>
        <AnimatePresence mode="wait">
          {state === 'idle' && (
            <motion.p
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-xs sm:text-sm text-gray-400"
            >
              Narrated in Jeslyn&rsquo;s voice — tap play to start.
            </motion.p>
          )}
          {isLoading && (
            <motion.p
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-xs sm:text-sm text-gray-400"
            >
              Preparing narration&hellip;
            </motion.p>
          )}
          {state === 'error' && (
            <motion.p
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-xs sm:text-sm text-red-300"
            >
              {error || 'Audio unavailable — try again.'}
            </motion.p>
          )}
          {canScrub && (
            <motion.div
              key="progress"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-1.5"
            >
              <div
                role="slider"
                aria-valuemin={0}
                aria-valuemax={duration || 0}
                aria-valuenow={current}
                onClick={seek}
                data-testid="reflection-audio-scrubber"
                className="h-1.5 rounded-full bg-white/8 overflow-hidden cursor-pointer"
              >
                <div
                  className="h-full bg-[#EF5B5B]"
                  style={{ width: duration ? `${(current / duration) * 100}%` : '0%' }}
                />
              </div>
              <div className="flex items-center justify-between text-[10px] sm:text-xs text-gray-500 font-mono tabular-nums">
                <span data-testid="reflection-audio-current">{fmt(current)}</span>
                <button
                  type="button"
                  onClick={restart}
                  className="inline-flex items-center gap-1 hover:text-[#EF5B5B] smooth"
                  data-testid="reflection-audio-restart"
                >
                  <RotateCcw className="w-3 h-3" /> Restart
                </button>
                <span data-testid="reflection-audio-duration">{fmt(duration)}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ReflectionAudioPlayer;
